import { useMemo } from 'react';

import type { TCreditPurposeFormFields } from 'entities/creditForm';

import {
  formatCreditTitle,
  formatCreditCardTitle,
  AccordionHeaderParamsTexts,
  CreditCardAccordionHeaderParamsTexts,
} from './formatCreditPurposeTitle';

type THeaderParams = {
  title: string;
  icon: string;
  description: string;
};

type DefaultParams = Partial<Pick<THeaderParams, 'title' | 'description'>>;

export type TCreditPurposeAccordionHeaderParamsOptions = {
  isCreditCardsForm?: boolean;
  productTabTitle?: string;
};

export const useAccordionHeaderParams = (
  data: Nullable<TCreditPurposeFormFields>,
  defaultData: DefaultParams,
  isOpen: boolean,
  options: TCreditPurposeAccordionHeaderParamsOptions = {},
) => {
  const { isCreditCardsForm, productTabTitle } = options;
  const defaultTitle = isCreditCardsForm
    ? CreditCardAccordionHeaderParamsTexts.HeaderDefaultTitle
    : AccordionHeaderParamsTexts.HeaderDefaultTitle;
  const defaultDescription = isCreditCardsForm
    ? CreditCardAccordionHeaderParamsTexts.HeaderDefaultDescription
    : AccordionHeaderParamsTexts.HeaderDefaultDescription;

  const { title = defaultData.title || defaultTitle, description = defaultData.description || defaultDescription } =
    useMemo((): Partial<THeaderParams> => {
      if (productTabTitle) {
        if (isOpen || !data) {
          return {
            title: productTabTitle,
            description: defaultDescription,
          };
        }

        return {
          title: isCreditCardsForm
            ? formatCreditCardTitle(data.amount, defaultDescription)
            : formatCreditTitle(data, defaultDescription),
          description: productTabTitle,
        };
      }

      if (!data || isOpen) {
        return {};
      }

      return {
        title: isCreditCardsForm
          ? formatCreditCardTitle(data.amount, defaultDescription)
          : formatCreditTitle(data, defaultDescription),
        description: isCreditCardsForm ? defaultTitle : data.purpose?.label,
      };
    }, [data, isOpen, defaultDescription, isCreditCardsForm, defaultTitle, productTabTitle]);

  return [title, description] as const;
};
